// Libraries
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { isNil, isNotNil } from 'ramda';

// MUI
import { useTheme, useMediaQuery, ImageList, ImageListItem } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add, Edit, Search } from '@mui/icons-material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { ADefaultButton } from '../../../common/buttons';
import AImageList from '../../../common/imageGallery/AImageList';
import AdditionalDialogActionButtons from '../../shared/components/dialog/AdditionalDialogActionButtons';
import EditIcon from '@mui/icons-material/Edit';

// Core
import { boundaryRepository } from '../../../core/boundaries';
import FormValidator from '../../../core/FormValidator';
import { ParameterValues, ResourceTextApplication } from '../../../core/resources';
import utils from '../../../core/utils';
import auth from '../../../core/authorization';
import AddAnimalDialog from '../../shared/components/AddAnimalDialog';
import DocumentAddDialog from '../../../documents/addDialog/DocumentAddDialog';
import ChainInformationDialog from '../../chain-information/components/ChainInformationDialog';
import ModifySalesTypeDialog from './sales-type/ModifySalesTypeDialog';
import MidSaveConfirmDialog from './general/MidSaveConfirmDialog';
import ModifyCollectWeekDialog from './general/ModifyCollectWeekDialog';

// Common
import {
    ADialogButtonsProp,
    ADialogContent,
    ADialogActions,
    ADialogButtonProp,
} from '../../../common/dialog';
import {
    AInputItem,
    AAutocompleteValue,
    AAutocomplete,
    AErrorTextInput,
    ATextInput,
    ADropdown,
    ALabel,
} from '../../../common/inputs';
import { Context } from '../../../common/contextSelector/ContextSelector';
import Annotation from '../../../common/annotations/Annotation';
import { SecondaryDialogTypeEnum } from '../../shared/types/enum';

// Interfaces
import {
    DialogModeEnum,
    AnnouncementTypeEnum,
    AnnouncementFlagsEnum,
} from '../../../interfaces/enums';
import { IAnimalSummary, IAnimalSummaryExtraInfo, IFarmAnimal } from '../../../interfaces/IAnimal';
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementAnimal,
    IAnimalAnnouncementBatch,
    IAnimalAnnouncementContainsAnimals,
    IAnimalAnnouncementContainsAnimalsResult,
    ICollectWeek,
    ICreateAnnouncement,
    IUpdateAnnouncementResult,
} from '../../../interfaces/IAnnouncement';
import { IFarmDetails } from '../../../interfaces/IBusinessEntities';
import { IApiResultMessage, ITiltuApiResult } from '../../../interfaces/IGeneral';
import { GUIDType } from '../../../interfaces/types';

// Store
import { useAppSelector } from '../../../store/hooks';

// API
import companyApi from '../../../api/companyApi';
import animalAnnouncementApi from '../../../api/animalAnnouncementApi';

// Feature - Animal Announcement

import { Strings } from '../../shared/constants/strings';
import { IGridSize } from './bovine-slaughter-announcement/BovineSlaughterAnnouncement';
import {
    hasOnlyExpressCow,
    omitAnimalAnnouncementAnimalsFromSelectableAnimals,
} from '../helpers/animal';
import {
    collectWeekStartNotValid,
    collectWeekStartHasValue,
    collectWeekEndHasValue,
    collectWeekEndNotValid,
    batchesHaveValidCollectWeek,
    defaultCollectWeek,
} from '../helpers/collectWeek';
import {
    getAnnouncementAnimals,
    hasAnnouncementResponseErrors,
    isFarmDetailsValidForSalesTypeUpdate,
    mapAnimalAnnouncementUpdate,
    mapAnimalsToAnimalAnnouncement,
    mapAnimalSummaryToIAnimalAnnouncementAnimal,
    mapAnimalSummaryToAnnouncementDialog,
    mapBovineInfos,
    mapPreliminaryIntermediationAnnouncementUpdatePayload,
    mapPreliminarySlaughterAnnouncementUpdatePayload,
    mapSelectedAnimalsToExistingAnimalAnnouncement,
} from '../helpers/data';
import {
    generateCollectWeekSequence,
    initializeCollectWeekStart,
    mapCollectWeeksForDropdown,
} from '../helpers/date';
import { removeDashes } from '../helpers/euIdentifier';
import { initializeHoldingSiteId, getHoldingSiteItems } from '../helpers/holdingSite';
import { getAllowedAnnouncementSalesTypes } from '../helpers/salesType';
import { getDistinctSalesTypeAnnotationMsgs } from '../helpers/salesTypeAnnotation';
import {
    constructAnnouncementNumberGridSize,
    constructCollectWeekGridSize,
} from '../../shared/helpers/gridSize';
import { isReadOnlyMode } from '../../shared/helpers/utils';
import AnimalAnnouncementDataGrid from './data-grid/AnimalAnnouncementDataGrid';
import { ISalesTypeDialogProps, IModifyCollectWeekDialogProps } from '../../shared/types/propTypes';
import '../styles/announcementStyles.scss';

import { IDocumentAddDialogData } from '../../../documents/IDocuments';
import defaultData from '../../../core/defaultData';
import {
    isDataValidForSave,
    isSalesTypeInvalidForSave,
} from '../../shared/validators/bovineAnnouncementValidators';
import { announcementDataService } from '../services/announcementDataService';
import documentApi from '../../../api/documentApi';
import { DocumentFileTypeEnum } from '../../../documents/IDocuments';
import {
    canCreateOrUpdateSlaughterAnnouncementWithoutChainInformation,
    canViewBovineSlaughterAnnouncementSaveAsAdmin,
    canCreateSlaughterAnnouncement,
    canViewBovineSlaughterAnnouncementDialog,
} from './bovine-slaughter-announcement/helpers/bovineSlaughterAnnouncementAccessRights';
import fileDownload from 'js-file-download';
import ConfirmDialog from '../../shared/components/confirm/ConfirmDialog';
import ProducerReturnDialog from '../../producer-return/ProducerRetrunDialog';
import producerReturnApi from '../../../api/producerReturnApi';
import { IProducerReturn } from '../../../interfaces/IProducerReturn';
import notificationApi from '../../../api/notificationApi';

export interface IAnnouncementDialogProps {
    data: IAnimalAnnouncement | null;
    announcementType: AnnouncementTypeEnum;
    onClose: (openPurchaseForConfirmation?: boolean) => void;
    handleDialogClose: () => void;
    mode: DialogModeEnum;
    selectedAnimals: IAnimalSummary[] | null;
    selectedHoldingSites?: string[];
    animals: IAnimalSummaryExtraInfo[] | null;
    processAnnouncementSave: (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string,
    ) => void;
    processAnnouncementDelete: (announcementId: GUIDType) => void;
    mapUpdatedSalesTypeToAllAnimalsData: (data: IFarmAnimal[]) => void;
    loading: boolean;
    canCreateAnimalAnnouncement: boolean | null;
    readOnly?: boolean;
    chainInformationFound?: boolean;
    dataImportedViaMmfi?: boolean;
    announcementConfirmation?: boolean;
    autoOpenProducerReturn?: boolean;
}

export default function AnimalAnnouncement(props: IAnnouncementDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaBovine',
        'AnelmaGeneral',
        'AnelmaCompany',
        'AnelmaAnimalAnnouncement',
        'AnelmaProducerReturn',
    ]);

    const theme = useTheme();
    const isBelowMdDevice = useMediaQuery(theme.breakpoints.down('md'));

    const context = useAppSelector((state) => state.context);
    const validator = new FormValidator();
    const { enqueueSnackbar } = useSnackbar();
    const userData = useAppSelector((state) => state.userData.data);

    const announcementType = props.announcementType;

    const [isLoading, setIsLoading] = useState<boolean>();
    const [announcementData, setAnnouncementData] = useState<IAnimalAnnouncement>(
        props.data
            ? JSON.parse(JSON.stringify(props.data))
            : mapAnimalsToAnimalAnnouncement(props.selectedAnimals || []),
    );
    const [farmDetails, setFarmDetails] = useState<IFarmDetails>();
    const [producerNumber, setProducerNumber] = useState<string>('');
    const [holdingSiteItems, setHoldingSiteItems] = useState<AInputItem<AAutocompleteValue>[]>([]);
    const [holdingSiteId, setHoldingSiteId] = useState<string>('');
    const [collectWeekStart, setCollectWeekStart] = useState<ICollectWeek>(
        initializeCollectWeekStart(announcementType),
    );

    //Jos collectWeekStart on vuoden viimeinen viikko, tulee collectWeekEnd olla seuraavan vuoden ensimm�inen viikko
    const getInitialCollectWeekEnd = (collectWeekStart: ICollectWeek): ICollectWeek => {
        const lastWeekOfYear = moment().year(collectWeekStart.Year).isoWeeksInYear();
        if (collectWeekStart.Week === lastWeekOfYear) {
            return {
                Year: collectWeekStart.Year + 1,
                Week: 1,
            };
        }
        return {
            Year: collectWeekStart.Year,
            Week: collectWeekStart.Week + 1,
        };
    };

    const [collectWeekEnd, setCollectWeekEnd] = useState<ICollectWeek>(
        getInitialCollectWeekEnd(collectWeekStart),
    );

    const [collectWeeks, setCollectWeeks] = useState<ICollectWeek[]>([]);

    const [addDialogVisibility, setAddDialogVisibility] = useState<boolean>(false);

    const [allowedSalesTypes, setAllowedSalesTypes] = useState<ParameterValues | null>(null);
    const [salesTypesAreValid, setSalesTypesAreValid] = useState<boolean>(false);
    const [holdingSiteIsValid, setHoldingSiteIsValid] = useState<boolean>(false);
    const [salesTypeAnnotations, setSalesTypeAnnotations] = useState<string[]>([]);

    const [selectedAnimals, setSelectedAnimals] = useState<IAnimalSummary[]>([]);
    const [animalsForDialog, setAnimalsForDialog] = useState<IAnimalSummary[] | null>(null);

    const [addInfoMinLength, setAddInfoMinLength] = useState<number | undefined>();
    const [addInfoMaxLength, setAddInfoMaxLength] = useState<number | undefined>();
    const [boundariesLoaded, setBoundariesLoaded] = useState<boolean>(false);

    const [addAnimalDialogVisible, setAddAnimalDialogVisible] = useState<boolean>(false);
    const [chainInformationDialogVisible, setChainInformationDialogVisible] =
        useState<boolean>(false);
    const [salesTypeDialogVisible, setSalesTypeDialogVisible] = useState<boolean>(false);
    const [collectWeekDialogVisible, setCollectWeekDialogVisible] = useState<boolean>(false);
    const [chainInfoConfirmDialogVisible, setChainInfoConfirmDialogVisible] =
        useState<boolean>(false);
    const [producerReturnDialogVisible, setProducerReturnDialogVisible] = useState<boolean>(false);

    const [collectWeekGridSize, setCollectWeekGridSize] = useState<IGridSize>({
        Xs: 2,
        Sm: 2,
        Md: 2,
        Lg: 2,
    });
    const [announcementNumberGridSize, setAnnouncementNumberGridSize] = useState<IGridSize>({
        Xs: 4,
        Sm: 4,
        Md: 4,
        Lg: 4,
    });
    const [readOnlyMode] = useState<boolean>(isReadOnlyMode(props.mode, props.readOnly ?? false));
    const [hideCollectWeeks, setHideCollectWeeks] = useState<boolean>(false);
    const [preview, setPreview] = useState<IDocumentAddDialogData>(defaultData.documentDetails);
    const [documentsToDelete, setDocumentsToDelete] = useState<IDocumentAddDialogData>(
        defaultData.documentDetails,
    );
    const [displayRejectDialog, setDisplayRejectDialog] = useState<boolean>(false);

    const [producerReturn, setProducerReturn] = useState<IProducerReturn | null>(null);
    const [producerReturnIsLoading, setProducerReturnIsLoading] = useState<boolean>(false);
    const [autoOpenProducerReturnHandled, setAutoOpenProducerReturnHandled] =
        useState<boolean>(false);

    const initializeFarmDetails = (context: Context) => {
        setIsLoading(true);
        const companyByContext = companyApi
            .getCompanyByContext(context.context)
            .then((response) => {
                setFarmDetails(response?.Entity as IFarmDetails);
            });
        Promise.all([companyByContext]).then(() => {
            loadProducerReturn();
            setIsLoading(false);
        });
        let producerId = utils.context.parseFarmContext(context.context)?.producerId;
        if (producerId) setProducerNumber(producerId);

        validateHoldingSite();
    };

    // Generate collect week sequences for the current and next year
    const initializeCollectWeeks = (from: number) => {
        setCollectWeeks(
            generateCollectWeekSequence(
                from,
                moment().isoWeeksInYear() - from,
                moment().isoWeekYear()
            )
        );
        if (moment().add(12, 'months').year() === moment().add(1, 'y').year()) {
            setCollectWeeks((previousState) => [
                ...previousState,
                ...generateCollectWeekSequence(
                    1,
                    moment().add(12, 'months').isoWeek(),
                    moment().isoWeekYear() + 1
                ),
            ]);
        }
    };

    useEffect(() => {
        boundaryRepository.load(['AnelmaAnimalAnnouncement']).then(() => {
            const boundaries = boundaryRepository.resource(
                'AnelmaAnimalAnnouncement',
                'BovinePurchaseAnnouncementInfo',
            );

            if (boundaries) {
                setAddInfoMaxLength(
                    Number.isInteger(boundaries.maxValue)
                        ? (boundaries.maxValue as number)
                        : undefined,
                );
                setAddInfoMinLength(
                    Number.isInteger(boundaries.minValue)
                        ? (boundaries.minValue as number)
                        : undefined,
                );
            }

            setBoundariesLoaded(true);
        });
    }, []);

    useEffect(() => {
        if (getAnnouncementAnimals(announcementData).length === 0)
            enqueueSnackbar(t('AnelmaBovine:1061'), {
                variant: 'error',
            });
        const start = initializeCollectWeekStart(props.announcementType);
        initializeCollectWeeks(start.Week);
        validateSalesTypes();
    }, [props.mode, props.readOnly, props.data, props.selectedAnimals]);

    useEffect(() => {
        if (context.data.currentContext) {
            initializeFarmDetails(context.data.currentContext);
        } else {
            setFarmDetails(undefined);
            setHoldingSiteIsValid(false);
        }
    }, [context]);

    useEffect(() => {
        resolveSalesTypeAnnotations(announcementData);
        validateSalesTypes();
        setHideCollectWeeks(hasOnlyExpressCow(props.announcementType, announcementData));
    }, [announcementData]);

    useEffect(() => {
        if (farmDetails && props.mode === DialogModeEnum.Create) {
            announcementContainsAnimal(announcementData.Batches.flatMap((b) => b.Animals));
        }
        setHoldingSiteId(
            props.selectedHoldingSites && props.selectedHoldingSites?.length === 1
                ? props.selectedHoldingSites[0]
                : initializeHoldingSiteId(
                    props.mode,
                    props.selectedAnimals,
                    farmDetails,
                    props.data?.HoldingSiteId
                )
        );

        if (farmDetails) {
            setHoldingSiteItems(getHoldingSiteItems(farmDetails));
            getAllowedAnnouncementSalesTypes(farmDetails, announcementType).then(
                (salesTypeParams) => {
                    setAllowedSalesTypes(salesTypeParams);
                },
            );
        }
    }, [farmDetails]);

    useEffect(() => {
        if (collectWeekStartNotValid(collectWeekStart, collectWeekEnd)) {
            if (
                collectWeekStartHasValue(collectWeekStart) &&
                collectWeekEndHasValue(collectWeekEnd)
            ) {
                enqueueSnackbar(t('AnelmaAnimalAnnouncement:1006'), {
                    variant: 'error',
                });
                setCollectWeekStart({ Year: -1, Week: -1 });
            }
        }
    }, [collectWeekStart]);

    useEffect(() => {
        if (announcementData.Id) {
            documentApi.downloadFilesByOwnerId(announcementData.Id).then((response) => {
                if (response) {
                    var fileArray: File[] = [];

                    for (var i = 0; i < response.Items.length; i++) {
                        var item = response.Items[i];
                        const file = base64ToFile(
                            item.Content,
                            item.FileName,
                            getMimeType(item.FileName),
                        );
                        fileArray.push(file);
                    }

                    if (response.Items.length > 0) {
                        let newData: IDocumentAddDialogData = {
                            displayName: response.Items[0].DisplayName,
                            file: fileArray,
                            owner: response.Items[0].Owner,
                            validFrom: response.Items[0].ValidFrom,
                            validDue: '',
                            type: response.Items[0].Type,
                        };

                        setPreview(newData);

                        let documentsToDelete: IDocumentAddDialogData = {
                            displayName: response.Items[0].DisplayName,
                            file: fileArray,
                            owner: response.Items[0].Owner,
                            validFrom: response.Items[0].ValidFrom,
                            validDue: '',
                            type: 8,
                        };

                        setDocumentsToDelete(documentsToDelete);
                    }
                }
            });
        }
    }, [announcementData.Id]);

    useEffect(() => {
        if (collectWeekEndNotValid(collectWeekEnd, collectWeekStart)) {
            if (collectWeekEndHasValue(collectWeekEnd)) {
                enqueueSnackbar(t('AnelmaAnimalAnnouncement:1007'), {
                    variant: 'error',
                });
                setCollectWeekEnd({ Year: -1, Week: -1 });
            }
        }
    }, [collectWeekEnd]);

    useEffect(() => {
        validateSalesTypes();
    }, [allowedSalesTypes]);

    useEffect(() => {
        validateHoldingSite();
    }, [holdingSiteId, holdingSiteItems]);

    useEffect(() => {
        if (isBelowMdDevice) {
            setAnnouncementNumberGridSize(constructAnnouncementNumberGridSize());
            setCollectWeekGridSize(constructCollectWeekGridSize());
        }
    }, [isBelowMdDevice]);

    useEffect(() => {
        // AN-2573 - Snellman admin user can create announcements anyway
        if (
            !animalAnnouncementCreationViaMmmfiIsValid(props.canCreateAnimalAnnouncement) &&
            !auth.canViewAdminContextSelector
        ) {
            enqueueSnackbar(t('AnelmaBovine:1092'), {
                variant: 'error',
            });
        }
    }, [props.canCreateAnimalAnnouncement]);

    useEffect(() => {
        if (
            props.autoOpenProducerReturn === true &&
            farmDetails &&
            !producerReturnIsLoading &&
            !autoOpenProducerReturnHandled
        ) {
            setProducerReturnDialogVisible(true);
            setAutoOpenProducerReturnHandled(true);
        }
    }, [
        farmDetails,
        producerReturnIsLoading,
        props.autoOpenProducerReturn,
        autoOpenProducerReturnHandled,
    ]);

    const resolveSalesTypeAnnotations = (announcementData?: IAnimalAnnouncement) => {
        setSalesTypeAnnotations(
            getDistinctSalesTypeAnnotationMsgs(announcementData).map((x) => t(x)),
        );
    };

    const handleAnimalDataChange = (params: string[]) => {
        if (props.animals) {
            setAddAnimalDialogVisible(false);

            const changedAnimals = mapAnimalSummaryToAnnouncementDialog(
                props.animals,
                params as GridRowSelectionModel,
            );

            announcementContainsAnimal(changedAnimals);

            setAnnouncementData(
                mapSelectedAnimalsToExistingAnimalAnnouncement(changedAnimals, announcementData),
            );
        }
    };

    const handleSalesTypeUpdate = (response: IFarmAnimal[]) => {
        mapUpdatedSalesTypeToAnnouncementData(response);
    };

    const mapUpdatedSalesTypeToAnnouncementData = (response: IFarmAnimal[]) => {
        if (announcementData) {
            let currentannouncementData: IAnimalAnnouncement = {
                ...announcementData,
            };
            for (const changedAnimal of response) {
                for (const batch of currentannouncementData.Batches) {
                    batch.Animals = batch.Animals.map((x) => ({ ...x }));
                    for (const animal of batch.Animals) {
                        if (changedAnimal.AnimalGUID === animal.AnimalId) {
                            animal.SalesType = changedAnimal.SalesType;

                            const collectWeek = defaultCollectWeek(batch.Animals[0]);
                            if (
                                collectWeek.Start.Year > 0 ||
                                props.mode === DialogModeEnum.Create
                            ) {
                                batch.CollectWeekStart = collectWeek.Start;
                                batch.CollectWeekEnd = collectWeek.End;
                            }
                        }
                    }
                }
            }
            setAnnouncementData(currentannouncementData);
        }
    };

    const removeAnimalFromAnnouncement = (
        animalData: IAnimalAnnouncementBatch,
        animalAnnouncementNumber?: number,
    ) => {
        if (!announcementData) return;

        // map the new announcement model from the animal data.
        const newAnnouncement = { ...announcementData };

        // using id when possible
        // especially on preliminary announcements that don't have batch number
        if (animalData.Id && animalData.Id.length > 0)
            newAnnouncement.Batches =
                announcementData?.Batches.filter((x) => x.Id !== animalData.Id) || [];
        // no id on announcement creation,
        // batch number is set to -1 -2 -3... on creation
        else
            newAnnouncement.Batches =
                announcementData?.Batches.filter((x) => x.BatchNumber !== animalData.BatchNumber) || [];

        setAnnouncementData(newAnnouncement);
    };

    const announcementContainsAnimal = async (animals: { EuIdentifier: string }[] | null) => {
        if (!farmDetails || !farmDetails.Id || !animals) return;

        const euIdentifierList = animals.map((animal) => removeDashes(animal.EuIdentifier));

        if (euIdentifierList.length <= 0) return;

        var payload: IAnimalAnnouncementContainsAnimals = {
            farmGuid: farmDetails.Id as GUIDType,
            announcementType: announcementType,
            euIdentifierList: euIdentifierList,
        };

        const data = await animalAnnouncementApi.animalAnnouncementContainsAnimals(payload);
        if (data) {
            const animalContainsData = data as unknown as IAnimalAnnouncementContainsAnimalsResult;

            if (animalContainsData.ResultSet.find((r) => r.IsInAnnouncement)) {
                switch (props.announcementType) {
                    case AnnouncementTypeEnum.Slaughter:
                    case AnnouncementTypeEnum.ManualBovineSlaughter:
                    case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                        enqueueSnackbar(t('AnelmaBovine:1096'), {
                            variant: 'warning',
                        });
                        break;
                    case AnnouncementTypeEnum.Intermediation:
                    case AnnouncementTypeEnum.ManualBovineIntermediation:
                    case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                        enqueueSnackbar(t('AnelmaBovine:1116'), {
                            variant: 'warning',
                        });
                        break;
                    case AnnouncementTypeEnum.BovinePurchase:
                        break;
                }
            }
        }
    };

    const validateSalesTypes = () => {
        if (!allowedSalesTypes) return setSalesTypesAreValid(true);

        for (let batch of announcementData.Batches) {
            for (let animal of batch.Animals) {
                const validSalesType = allowedSalesTypes.find(
                    (x) => Number(x.code) === Number(animal.SalesType),
                );
                if (!validSalesType) {
                    setSalesTypesAreValid(false);
                    return;
                }
            }
        }
        setSalesTypesAreValid(true);
    };

    const validateHoldingSite = () => {
        // First if avoids error state on loading
        if (farmDetails && holdingSiteId) {
            if (
                holdingSiteId.length > 0 &&
                farmDetails.HoldingSites.findIndex((h) => h.HoldingSiteId === holdingSiteId) != -1
            )
                return setHoldingSiteIsValid(true);

            return setHoldingSiteIsValid(false);
        }
    };

    const additionalActionButtonVisibility = (): boolean => {
        if (
            !batchesHaveValidCollectWeek(announcementData?.Batches ?? [], props.mode) ||
            !auth.canViewBovineBrokerSales ||
            isLoading ||
            (props.readOnly && props.readOnly === true)
        )
            return true;

        if (props.mode === DialogModeEnum.Create && auth.canCreateBovineBrokerSales) return false;
        else if (props.mode === DialogModeEnum.Edit && auth.canModifyBovineBrokerSales)
            return false;

        return true;
    };

    const saveActionButtonVisibility = (): boolean => {
        return additionalActionButtonVisibility()
            ? true
            : farmDetails !== undefined &&
                farmDetails.HoldingSites !== null &&
                farmDetails.HoldingSites.length > 0 &&
                farmDetails.HoldingSites[0].HoldingSiteId === ''
            ? true
            : false;
    };

    const displayGeneralSnackbarError = () => {
        enqueueSnackbar(t('AnelmaGeneral:1030'), {
            variant: 'error',
        });
    };

    const openChainInformationDialog = () => {
        if (readOnlyMode !== true) {
            setChainInfoConfirmDialogVisible(true);
        } else {
            setChainInformationDialogVisible(true);
        }
    };

    const deleteBatches = async () => {
        if (!props.data) return false;

        let ok = true;
        setIsLoading(true);

        for (let batch of props.data.Batches) {
            const isDeleted = !announcementData.Batches.find((b) => b.Id === batch.Id);
            if (!isDeleted) continue;

            var response = null;

            if (batch.BatchNumber)
                response = await animalAnnouncementApi.deleteAnimalAnnouncementBatch(
                    announcementData.Number,
                    batch.BatchNumber as unknown as string[],
                );
            else
                response = await animalAnnouncementApi.deleteAnimalAnnouncementBatchesByIds(
                    announcementData.Id,
                    [batch.Id],
                );

            if (!response) {
                ok = false;
                enqueueSnackbar(t('#Issue when deleting batch from announcement'), {
                    variant: 'error',
                });
            }
            if (!ok) break;
            props.data.Batches = props.data.Batches.filter((b) => b.Id !== batch.Id);
        }

        setIsLoading(false);
        return ok;
    };

    const commitSalesTypeChanges = () => {
        if (!farmDetails || !farmDetails.Id) return;

        const updates: { [key: string]: IFarmAnimal[] } = {};
        for (let animal of announcementData.Batches.map((b) => b.Animals[0])) {
            const original = props.animals?.find((x) => x.EuIdentifier === animal.EuIdentifier);
            if (!original || original?.SalesType === animal.SalesType) continue;
            original.SalesType = animal.SalesType;

            const key = String(animal.SalesType);
            if (!updates[key]) updates[key] = [];

            updates[key].push({
                AnimalGUID: animal.AnimalId,
                AnimalType: 1,
                ExternalIdentifier: animal.EuIdentifier,
                FarmId: farmDetails.Id,
                SalesType: animal.SalesType,
                ValidFrom: new Date(announcementData.Created),
                ValidTo: null,
            });
        }

        if (props.data) props.data.Batches = announcementData.Batches;

        for (let key in updates) {
            const update = updates[key];
            props.mapUpdatedSalesTypeToAllAnimalsData(update);
        }
    };

    const canSave = () => {
        if (props.mode === DialogModeEnum.Create)
            return (
                isDataValidForSave(
                    props.mode,
                    farmDetails?.Id,
                    producerNumber,
                    announcementData?.Batches,
                    collectWeekStart,
                    collectWeekEnd,
                    null,
                    holdingSiteId,
                ) && announcementData.Batches.length > 0
            );
        return true;
    };

    const loadProducerReturn = () => {
        if (announcementData.Id.length > 0) {
            setProducerReturnIsLoading(true);

            producerReturnApi
                .getProducerReturnByAnnouncement(announcementData.Id)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        });

                        return;
                    }

                    setProducerReturn(response.Entity);
                })
                .finally(() => setProducerReturnIsLoading(false));
        }
    };

    const handleSaveCreate = async (openChainInformation: boolean) => {
        if (
            isDataValidForSave(
                props.mode,
                farmDetails?.Id,
                producerNumber,
                announcementData?.Batches,
                collectWeekStart,
                collectWeekEnd,
                null,
                holdingSiteId,
            )
        ) {
            const selectedAnimals = getAnnouncementAnimals(announcementData);
            const bovineInfos = mapBovineInfos(
                selectedAnimals,
                holdingSiteId,
                collectWeekStart,
                collectWeekEnd,
            );
            if (bovineInfos.find((_) => isSalesTypeInvalidForSave(_.SalesType))) {
                enqueueSnackbar(t('AnelmaBovine:1133'), {
                    variant: 'error',
                });
                setIsLoading(false);
                return;
            }
            const payload: ICreateAnnouncement = {
                ProducerNumber: Number(producerNumber),
                AnnouncementInfo: announcementData?.Info || '',
                FarmId: farmDetails?.Id as GUIDType,
                BovineInfos: bovineInfos,
                HasFiles: preview.file.length > 0,
            };

            if (payload.BovineInfos.length > 0) {
                setIsLoading(true);
                announcementDataService
                    .createAnnouncementCall(payload, announcementType)
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                                variant: 'error',
                            });
                            setIsLoading(false);
                            return;
                        }
                        setIsLoading(false);
                        if (hasAnnouncementResponseErrors(response)) {
                            response.Summary.Errors.map((_) => {
                                enqueueSnackbar(`${_.Explanation}`, {
                                    variant: 'error',
                                });
                            });
                            return;
                        } else if (
                            !hasAnnouncementResponseErrors(response) &&
                            openChainInformation &&
                            announcementData
                        ) {
                            commitSalesTypeChanges();
                            props.processAnnouncementSave(
                                response.Entity,
                                AnnouncementTypeEnum.SlaughterWithChainInformation,
                                holdingSiteId,
                            );
                            announcementData.Id = response.Entity.AnnouncementId;
                            announcementData.Number = Number(response.Entity.AnnouncementNumber);
                            setChainInformationDialogVisible(true);
                        } else if (!hasAnnouncementResponseErrors(response)) {
                            commitSalesTypeChanges();
                            props.processAnnouncementSave(
                                response.Entity,
                                announcementType,
                                holdingSiteId,
                            );
                            enqueueSnackbar(t('AnelmaGeneral:1018'), {
                                variant: 'success',
                            });
                            props.onClose();
                        }

                        if (preview.file.length > 0)
                            documentApi.uploadDocuments(
                                preview,
                                DocumentFileTypeEnum.Image,
                                response.Entity.AnnouncementId,
                            );
                    })
                    .catch((error: IApiResultMessage[]) => {
                        if (error && error?.length > 0) {
                            error?.forEach((err) =>
                                enqueueSnackbar(err?.Explanation, {
                                    variant: 'error',
                                }),
                            );
                        }
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            } else setIsLoading(false);
        } else displayGeneralSnackbarError();
    };

    const handleSaveUpdate = async (openChainInformation: boolean) => {
        if (
            isDataValidForSave(
                props.mode,
                farmDetails?.Id,
                producerNumber,
                null,
                collectWeekStart,
                collectWeekEnd,
                announcementData?.Number,
                holdingSiteId,
            ) &&
            props.data?.Batches &&
            announcementData !== null &&
            batchesHaveValidCollectWeek(props.data?.Batches ?? [], props.mode) &&
            farmDetails &&
            farmDetails?.Id
        ) {
            const payload = mapAnimalAnnouncementUpdate(
                announcementData,
                props.data.Batches,
                producerNumber,
                farmDetails.Id,
                holdingSiteId,
                preview.file.length > 0,
            );

            var batchesDeleted = await deleteBatches();
            if (!batchesDeleted) return;

            setIsLoading(true);
            announcementDataService
                .updateAnnouncementCall(payload, announcementType)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                            variant: 'error',
                        });
                        setIsLoading(false);
                        return;
                    }
                    if (hasAnnouncementResponseErrors(response)) {
                        response.Summary.Errors.map((_) => {
                            enqueueSnackbar(`${_.Explanation}`, {
                                variant: 'error',
                            });
                        });
                        setIsLoading(false);
                        return;
                    }
                    commitSalesTypeChanges();
                    setIsLoading(false);

                    props.processAnnouncementSave(response.Entity, announcementType, holdingSiteId);

                    if (openChainInformation) {
                        setChainInformationDialogVisible(true);
                    } else {
                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });
                        props.onClose();
                    }

                    if (preview.file.length > 0) {
                        documentApi.uploadDocuments(
                            preview,
                            DocumentFileTypeEnum.Image,
                            response.Entity.AnnouncementId,
                        );
                    } else if (documentsToDelete.file.length > 0) {
                        documentApi.uploadDocuments(
                            documentsToDelete,
                            DocumentFileTypeEnum.Image,
                            response.Entity.AnnouncementId,
                        );
                    }
                })
                .catch((error: IApiResultMessage[]) => {
                    if (error) {
                        error.forEach((e) =>
                            enqueueSnackbar(e?.Explanation ?? e, {
                                variant: 'error',
                            }),
                        );
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
            setIsLoading(false);
        }
    };

    const handleSave = (openChainInformation: boolean) => {
        if (!salesTypesAreValid) {
            displayGeneralSnackbarError();
            return;
        }
        if (props.mode === DialogModeEnum.Create) {
            handleSaveCreate(openChainInformation);
        } else if (props.mode === DialogModeEnum.Edit) {
            handleSaveUpdate(openChainInformation);
        }
    };

    const updatePreliminaryBovineSlaughterAnnouncement = async (
        flags: AnnouncementFlagsEnum,
        openChainInformation: boolean
    ) => {
        if (
            isDataValidForSave(
                props.mode,
                farmDetails?.Id,
                producerNumber,
                null,
                collectWeekStart,
                collectWeekEnd,
                announcementData?.Number,
                holdingSiteId
            ) &&
            props.data?.Batches &&
            announcementData !== null &&
            batchesHaveValidCollectWeek(props.data?.Batches ?? [], props.mode) &&
            farmDetails &&
            farmDetails?.Id
        ) {
            const payload = mapPreliminarySlaughterAnnouncementUpdatePayload(
                announcementData,
                props.data.Batches,
                producerNumber,
                farmDetails.Id,
                holdingSiteId,
                preview.file.length > 0,
                flags
            );

            let batchesDeleted = await deleteBatches();
            if (!batchesDeleted) return;

            setIsLoading(true);
            announcementDataService
                .updatePreliminaryBovineSlaughterAnnouncementCall(payload)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                            variant: 'error',
                        });
                        setIsLoading(false);
                        return;
                    }
                    if (hasAnnouncementResponseErrors(response)) {
                        response.Summary.Errors.map((_) => {
                            enqueueSnackbar(`${_.Explanation}`, {
                                variant: 'error',
                            });
                        });
                        setIsLoading(false);
                        return;
                    }
                    commitSalesTypeChanges();
                    setIsLoading(false);
                    if (payload.Additions.length > 0) {
                        response.Entity.Status = '1';
                    }
                    props.processAnnouncementSave(response.Entity, AnnouncementTypeEnum.Slaughter, holdingSiteId);

                    if (openChainInformation) {
                        setChainInformationDialogVisible(true);
                    } else {
                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });
                        props.onClose(true);
                    }

                    if (preview.file.length > 0) {
                        documentApi.uploadDocuments(
                            preview,
                            DocumentFileTypeEnum.Image,
                            response.Entity.AnnouncementId
                        );
                    } else if (documentsToDelete.file.length > 0) {
                        documentApi.uploadDocuments(
                            documentsToDelete,
                            DocumentFileTypeEnum.Image,
                            response.Entity.AnnouncementId
                        );
                    }
                })
                .catch((error: IApiResultMessage[]) => {
                    if (error) {
                        error.forEach((e) =>
                            enqueueSnackbar(e?.Explanation ?? e, {
                                variant: 'error',
                            })
                        );
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
            setIsLoading(false);
        }
    };

    const updatePreliminaryBovineIntermediationAnnouncement = async (
        flags: AnnouncementFlagsEnum
    ) => {
        if (
            isDataValidForSave(
                props.mode,
                farmDetails?.Id,
                producerNumber,
                null,
                collectWeekStart,
                collectWeekEnd,
                announcementData?.Number,
                holdingSiteId
            ) &&
            props.data?.Batches &&
            announcementData !== null &&
            batchesHaveValidCollectWeek(props.data?.Batches ?? [], props.mode) &&
            farmDetails &&
            farmDetails?.Id
        ) {
            const payload = mapPreliminaryIntermediationAnnouncementUpdatePayload(
                announcementData,
                props.data.Batches,
                producerNumber,
                farmDetails.Id,
                holdingSiteId,
                preview.file.length > 0,
                flags
            );

            let batchesDeleted = await deleteBatches();
            if (!batchesDeleted) return;

            setIsLoading(true);
            announcementDataService
                .updatePreliminaryBovineIntermediationAnnouncementCall(payload)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                            variant: 'error',
                        });
                        setIsLoading(false);
                        return;
                    }
                    if (hasAnnouncementResponseErrors(response)) {
                        response.Summary.Errors.map((_) => {
                            enqueueSnackbar(`${_.Explanation}`, {
                                variant: 'error',
                            });
                        });
                        setIsLoading(false);
                        return;
                    }
                    commitSalesTypeChanges();
                    setIsLoading(false);
                    if (payload.Additions.length > 0) {
                        response.Entity.Status = '1';
                    }
                    props.processAnnouncementSave(response.Entity, AnnouncementTypeEnum.Intermediation, holdingSiteId);

                    if (preview.file.length > 0) {
                        documentApi.uploadDocuments(
                            preview,
                            DocumentFileTypeEnum.Image,
                            response.Entity.AnnouncementId
                        );
                    } else if (documentsToDelete.file.length > 0) {
                        documentApi.uploadDocuments(
                            documentsToDelete,
                            DocumentFileTypeEnum.Image,
                            response.Entity.AnnouncementId
                        );
                    }

                    enqueueSnackbar(t('AnelmaGeneral:1018'), {
                        variant: 'success',
                    });
                    props.onClose(true);
                })
                .catch((error: IApiResultMessage[]) => {
                    if (error) {
                        error.forEach((e) =>
                            enqueueSnackbar(e?.Explanation ?? e, {
                                variant: 'error',
                            })
                        );
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
            setIsLoading(false);
        }
    };

    const rejectPreliminaryAnnouncement = (id: GUIDType) => {
        animalAnnouncementApi
            .rejectPreliminaryAnnouncement(id)
            .then((response) => {
                if (!response) {
                    enqueueSnackbar(t('AnelmaGeneral:1019'), {
                        variant: 'error',
                    });

                    return;
                }

                enqueueSnackbar(t('AnelmaGeneral:1018'), {
                    variant: 'success',
                });

                props.processAnnouncementDelete(id);
                props.onClose(true);
            })
            .catch((error: IApiResultMessage[]) => {
                if (error) {
                    error.forEach((e) =>
                        enqueueSnackbar(e?.Explanation ?? e, {
                            variant: 'error',
                        })
                    );
                }
            })
    };

    const sendPreliminaryAnnouncementNotification = (announcementId: GUIDType) => {
        notificationApi
            .sendPreliminaryAnnouncementNotification(announcementData.Id)
            .then((response) => {
                if (!response)
                    enqueueSnackbar(t('AnelmaGeneral:1019'), {
                        variant: 'error',
                    });

                enqueueSnackbar(t('AnelmaBovine:1236'), {
                    variant: 'success',
                });
            })
            .catch((error: IApiResultMessage[]) => {
                if (error) {
                    error.forEach((e) =>
                        enqueueSnackbar(e?.Explanation ?? t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        })
                    );
                }
            });
    }

    const animalAnnouncementCreationViaMmmfiIsValid = (value: boolean | null): boolean => {
        return value === null ? true : value;
    };

    const producerReturnButtonDisabled = () => {
        if (props.mode === DialogModeEnum.Create) return true;

        if (producerReturn) return !auth.canViewProducerReturn;
        else
            return (
                producerReturnIsLoading ||
                selectedAnimals.length !== 1 ||
                !auth.canCreateProducerReturn
            );
    };

    const salesTypeDialogProps: ISalesTypeDialogProps = {
        id: announcementData.Id,
        onClose: () => {
            setAnimalsForDialog(null);
            setSalesTypeDialogVisible(false);
        },
        animals: mapAnimalSummaryToIAnimalAnnouncementAnimal(
            animalsForDialog || selectedAnimals,
        ),
        allAnimals: null,
        handleSalesTypeUpdate: handleSalesTypeUpdate,
        farmId: isFarmDetailsValidForSalesTypeUpdate(farmDetails),
        farmDetails: farmDetails,
        announcementType: announcementType,
        mode: props.mode,
    };

    const isPreliminaryAnnouncement = (announcemeentType: AnnouncementTypeEnum) => {
        return announcemeentType == AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
            || announcemeentType == AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            || announcemeentType == AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement;
    }

    const modifyCollectWeekDialogProps: IModifyCollectWeekDialogProps = {
        onClose: () => {
            setCollectWeekDialogVisible(false);
        },
        announcementType: announcementType,
        animals: mapAnimalSummaryToIAnimalAnnouncementAnimal(selectedAnimals),
        batches:
            announcementData !== null && props.mode === DialogModeEnum.Edit
                ? announcementData.Batches
                : undefined,
        showYear: isPreliminaryAnnouncement(announcementType)
    };

    const getAnnouncementRejectButton = (): ADialogButtonProp => {
        return {
            onClick: () => setDisplayRejectDialog(true),
            type: 'default',
            label: t('AnelmaBovine:1201'),
            disabled: isLoading === true || readOnlyMode,
        };
    };

    const getSlaughterAnnouncementConfirmationButton = (): ADialogButtonProp => {
        return {
            onClick: () => {
                if (validator.invalid) {
                    displayGeneralSnackbarError();
                    return;
                }
                let confimedAnnouncementFlag = 2;
                updatePreliminaryBovineSlaughterAnnouncement(
                    confimedAnnouncementFlag,
                    !auth.canCreateBovineSlaughterAnnouncementAdmin
                );
            },
            type: 'save',
            label: auth.canCreateBovineSlaughterAnnouncementAdmin
                ? t('AnelmaBovine:1132')
                : t('AnelmaBovine:1197'),
            disabled:
                readOnlyMode ||
                !salesTypesAreValid ||
                isLoading ||
                !canSave() ||
                saveActionButtonVisibility() ||
                canCreateSlaughterAnnouncement(auth) ||
                canViewBovineSlaughterAnnouncementDialog(auth),
        };
    };

    const getSlaughterAnnouncementChainInformationCreateBtnForProducer = (): ADialogButtonProp => {
        return {
            label: readOnlyMode ? t('AnelmaBovine:1158') : t('AnelmaBovine:1050'),
            icon: readOnlyMode ? <Search /> : <Edit />,
            style: {
                marginRight: 'auto',
                marginLeft: '65px',
                visibility: 'hidden',
            },
            onClick: () => openChainInformationDialog(),
            type: 'default',
            disabled:
                props.mode === DialogModeEnum.Create || isLoading
                    ? true
                    : props.mode === DialogModeEnum.Edit || isLoading
                        ? true
                        : false,
        };
    };

    const getSlaughterAnnouncementChainInformationEditBtnForProducer = (): ADialogButtonProp => {
        return {
            label: readOnlyMode ? t('AnelmaBovine:1158') : t('AnelmaBovine:1050'),
            icon: readOnlyMode ? <Search /> : <Edit />,
            style: { marginRight: 'auto', marginLeft: '65px' },
            onClick: () => openChainInformationDialog(),
            type: 'default',
            disabled: props.chainInformationFound ? true : isLoading || !holdingSiteIsValid,
        };
    };

    const getIntermediationAnnouncementConfirmationButton = (): ADialogButtonProp => {
        return {
            onClick: () => {
                if (validator.invalid) {
                    displayGeneralSnackbarError();
                    return;
                }
                let confimedAnnouncementFlag = 2;
                updatePreliminaryBovineIntermediationAnnouncement(confimedAnnouncementFlag);
            },
            type: 'save',
            label: t('AnelmaBovine:1200'),
            disabled:
                !salesTypesAreValid ||
                saveActionButtonVisibility() ||
                !canSave() ||
                !holdingSiteIsValid,
        };
    };

    const getIntermediationAnnouncementActionButtons = () => {
        let dialogActionButtons_intermediation: ADialogButtonsProp = {
            left: [
                {
                    label: t('AnelmaGeneral:1009'),
                    onClick: () => props.handleDialogClose(),
                    type: 'cancel',
                },
                {
                    label: t('AnelmaBovine:1049'),
                    icon: <Add />,
                    onClick: () => setAddAnimalDialogVisible(true),
                    type: 'default',
                    disabled:
                        additionalActionButtonVisibility() ||
                        !holdingSiteIsValid ||
                        !animalAnnouncementCreationViaMmmfiIsValid(props.canCreateAnimalAnnouncement),
                },
            ],
            right: []
        };

        if (announcementType !== AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) {
            dialogActionButtons_intermediation.right.push({
                onClick: handleSave,
                type: 'save',
                disabled:
                    !salesTypesAreValid ||
                    saveActionButtonVisibility() ||
                    !canSave() ||
                    !holdingSiteIsValid ||
                    !animalAnnouncementCreationViaMmmfiIsValid(
                        props.canCreateAnimalAnnouncement
                    ),
            });
        }
        else {
            // snellman users only
            if (auth.canViewAdminContextSelector) {
                dialogActionButtons_intermediation.left.push({
                    label: t('AnelmaBovine:1228'),
                    onClick: () => { sendPreliminaryAnnouncementNotification(announcementData.Id) },
                    type: 'default'
                })
            }

            dialogActionButtons_intermediation.right.push(
                getAnnouncementRejectButton(),
                getIntermediationAnnouncementConfirmationButton()
            );
        }

        return dialogActionButtons_intermediation;
    };

    const getSlaughterAnnouncementActionButtons = () => {
        const dialogActionButtons_slaughter: ADialogButtonsProp = {
            left: [
                {
                    label: t('AnelmaGeneral:1009'),
                    onClick: () => props.handleDialogClose(),
                    type: 'cancel',
                },
            ],
            right: [],
        };

        if (canViewBovineSlaughterAnnouncementSaveAsAdmin(auth)) {
            dialogActionButtons_slaughter.left.push({
                label: t('AnelmaBovine:1049'),
                icon: <Add />,
                onClick: () => setAddAnimalDialogVisible(true),
                type: 'default',
                disabled:
                    isLoading ||
                    canCreateOrUpdateSlaughterAnnouncementWithoutChainInformation(auth) ||
                    !holdingSiteIsValid ||
                    readOnlyMode ||
                    !animalAnnouncementCreationViaMmmfiIsValid(props.canCreateAnimalAnnouncement),
            });

            if (props.mode === DialogModeEnum.Edit) {
                dialogActionButtons_slaughter.left.push({
                    label: readOnlyMode ? t('AnelmaBovine:1158') : t('AnelmaBovine:1050'),
                    icon: readOnlyMode ? <Search /> : <Edit />,
                    onClick: () => openChainInformationDialog(),
                    type: 'default',
                    disabled: props.chainInformationFound ? true : isLoading || !holdingSiteIsValid,
                });
            }

            if (props.announcementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
                // snellman users only
                && auth.canViewAdminContextSelector) {
                dialogActionButtons_slaughter.left.push({
                    label: t('AnelmaBovine:1228'),
                    onClick: () => { sendPreliminaryAnnouncementNotification(announcementData.Id) },
                    type: 'default'
                })
            }
            /* else {
                dialogActionButtons_slaughter.left.push({
                    // Left in code so it takes space on screen, even if it is hidden and if disable logic is wanted back
                    label: readOnlyMode ? t('AnelmaBovine:1158') : t('AnelmaBovine:1050'),
                    icon: readOnlyMode ? <Search /> : <Edit />,
                    style: {
                        marginRight: 'auto',
                        marginLeft: '65px',
                        visibility: 'hidden',
                    },
                    onClick: () => openChainInformationDialog(),
                    type: 'default',
                    disabled:
                        props.mode === DialogModeEnum.Create || isLoading
                            ? true
                            : props.mode === DialogModeEnum.Edit || isLoading
                                ? true
                                : false,
                });
            } */
        } else {
            dialogActionButtons_slaughter.left.push({
                label: t('AnelmaBovine:1049'),
                icon: <Add />,
                onClick: () => setAddAnimalDialogVisible(true),
                type: 'default',
                disabled:
                    readOnlyMode ||
                    !holdingSiteIsValid ||
                    isLoading ||
                    (canCreateSlaughterAnnouncement(auth)
                        ? true
                        : canViewBovineSlaughterAnnouncementDialog(auth)) ||
                    !animalAnnouncementCreationViaMmmfiIsValid(props.canCreateAnimalAnnouncement),
            });

            if (props.mode === DialogModeEnum.Edit) {
                dialogActionButtons_slaughter.left.push({
                    label: readOnlyMode ? t('AnelmaBovine:1158') : t('AnelmaBovine:1050'),
                    icon: readOnlyMode ? <Search /> : <Edit />,
                    onClick: () => openChainInformationDialog(),
                    type: 'default',
                    disabled: props.chainInformationFound ? true : isLoading || !holdingSiteIsValid,
                });
            }
            /* else {
                dialogActionButtons_slaughter.left.push({
                    // Left in code so it takes space on screen, even if it is hidden and if disable logic is wanted back
                    label: readOnlyMode ? t('AnelmaBovine:1158') : t('AnelmaBovine:1050'),
                    icon: readOnlyMode ? <Search /> : <Edit />,
                    style: {
                        marginRight: 'auto',
                        marginLeft: '65px',
                        visibility: 'hidden',
                    },
                    onClick: () => openChainInformationDialog(),
                    type: 'default',
                    disabled:
                        props.mode === DialogModeEnum.Create || isLoading
                            ? true
                            : props.mode === DialogModeEnum.Edit || isLoading
                                ? true
                                : false,
                });
            } */
        }

        dialogActionButtons_slaughter.left.push({
            label: t('AnelmaProducerReturn:1000'),
            onClick: () => setProducerReturnDialogVisible(true),
            type: producerReturn ? 'default' : 'with-tooltip',
            disabled: producerReturnButtonDisabled(),
            tooltip: props.mode == DialogModeEnum.Create
                ? t('AnelmaBovine:1219')
                : t('AnelmaBovine:1218')
        });

        if (announcementType !== AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) {
            if (canViewBovineSlaughterAnnouncementSaveAsAdmin(auth)) {
                dialogActionButtons_slaughter.right.push({
                    onClick: () => {
                        if (validator.invalid) {
                            displayGeneralSnackbarError();
                            return;
                        }
                        handleSave(false);
                    },
                    type: 'save',
                    label: t('AnelmaBovine:1132'),
                    disabled:
                        readOnlyMode ||
                        !salesTypesAreValid ||
                        isLoading ||
                        !canSave() ||
                        saveActionButtonVisibility() ||
                        canCreateOrUpdateSlaughterAnnouncementWithoutChainInformation(auth) ||
                        !animalAnnouncementCreationViaMmmfiIsValid(
                            props.canCreateAnimalAnnouncement
                        ),
                });
            }
            else {
                dialogActionButtons_slaughter.right.push({
                    onClick: () => {
                        if (validator.invalid) {
                            displayGeneralSnackbarError();
                            return;
                        }
                        handleSave(true);
                    },
                    type: 'save',
                    label:
                        props.mode === DialogModeEnum.Edit
                            ? t('AnelmaGeneral:1001')
                            : t('AnelmaBovine:1131'),
                    disabled:
                        readOnlyMode ||
                        !salesTypesAreValid ||
                        isLoading ||
                        !canSave() ||
                        saveActionButtonVisibility() ||
                        (canCreateSlaughterAnnouncement(auth)
                            ? true
                            : canViewBovineSlaughterAnnouncementDialog(auth)) ||
                        !animalAnnouncementCreationViaMmmfiIsValid(
                            props.canCreateAnimalAnnouncement
                        ),
                });
            }
        }
        else {
            dialogActionButtons_slaughter.right.push(
                getAnnouncementRejectButton(),
                getSlaughterAnnouncementConfirmationButton()
            );
        }

        if (
            !canViewBovineSlaughterAnnouncementSaveAsAdmin(auth) &&
            props.mode === DialogModeEnum.Create
        ) {
            dialogActionButtons_slaughter.left.push(
                getSlaughterAnnouncementChainInformationCreateBtnForProducer()
            );
        }
        if (
            !canViewBovineSlaughterAnnouncementSaveAsAdmin(auth) &&
            props.mode === DialogModeEnum.Edit &&
            isNil(props.announcementConfirmation)
        ) {
            dialogActionButtons_slaughter.left.push(
                getSlaughterAnnouncementChainInformationEditBtnForProducer()
            );
        }

        return dialogActionButtons_slaughter;
    };

    const filesToSaveHandler = (data: IDocumentAddDialogData) => {
        data.owner = announcementData.Id; // For announcements announcement needs to be file owner instead of the user.
        setPreview(data);
        setAddDialogVisibility(false);
        return Promise.resolve(null);
    };

    const base64ToFile = (base64String: string, fileName: string, mimeType: string) => {
        const byteString = atob(base64String);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([ab], { type: mimeType });
        return new File([blob], fileName, { type: mimeType });
    };

    const getMimeType = (fileName: string): string => {
        const mimeTypes: { [key: string]: string } = {
            '.jpg': 'image/jpeg',
            '.jpeg': 'image/jpeg',
            '.png': 'image/png',
            '.gif': 'image/gif',
            '.pdf': 'application/pdf',
            '.txt': 'text/plain',
            '.doc': 'application/msword',
            '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            '.xls': 'application/vnd.ms-excel',
            '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            // Add more mappings as needed
        };

        return (
            mimeTypes[getFileExtension(fileName).toLocaleLowerCase()] || 'application/octet-stream'
        );
    };

    const downLoadImage = (file: File) => {
        let fileName: string = file.name;

        fileDownload(file, fileName.trim());
    };

    const getFileExtension = (filename: string): string => {
        const parts = filename.split('.');
        return parts.length > 1 ? `.${parts.pop()}` : '';
    };

    const producerRetrunAnimalIsLO = () => {
        if (producerReturn) {
            let i = announcementData.Batches.findIndex(
                (a) =>
                    a.Animals.length === 1 &&
                    a.Animals[0].EuIdentifier === producerReturn.EUIdentifier,
            );

            return announcementData.Batches[i].BatchStatus === 'LO';
        }

        return false;
    };

    const getSelectedAnimalCollectDate = () => {
        if (selectedAnimals.length === 1) {
            let i = announcementData.Batches.findIndex(
                (b) => b.Animals[0].AnimalId === selectedAnimals[0].Id,
            );
            let collectWeek = announcementData.Batches[i].CollectWeekEnd;

            if (i != -1) {
                return moment()
                    .year(collectWeek.Year)
                    .startOf('year')
                    .add(collectWeek.Week - 1, 'week')
                    .startOf('week');
            }
        }

        return moment();
    };

    const dialogActionButtons =
        announcementType === AnnouncementTypeEnum.Slaughter ||
        announcementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            ? getSlaughterAnnouncementActionButtons()
            : getIntermediationAnnouncementActionButtons();

    const renderHoldingSiteComponent = () => {
        if (
            props.mode === DialogModeEnum.Create &&
            (holdingSiteId === '' || holdingSiteId === null) &&
            holdingSiteItems.length > 0
        ) {
            return (
                <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8 }}>
                    <AAutocomplete
                        id={Strings.AnnouncementHoldingSiteId}
                        items={holdingSiteItems}
                        label={t('AnelmaBovine:1063')}
                        onChange={(v) => setHoldingSiteId(v?.toString() ?? '')}
                        validator={validator}
                        required
                        value={holdingSiteId}
                        error={!holdingSiteIsValid}
                    />
                </Grid>
            );
        } else if (
            props.mode === DialogModeEnum.Create &&
            (holdingSiteId !== '' || holdingSiteId !== null)
        ) {
            return holdingSiteIsValid ? (
                <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8 }}>
                    <ATextInput
                        id={Strings.AnnouncementHoldingSiteId}
                        label={t('AnelmaBovine:1063')}
                        onChange={(v) => false}
                        validator={validator}
                        disabled
                        value={holdingSiteId}
                    />
                </Grid>
            ) : (
                <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8 }}>
                    <AErrorTextInput
                        id={Strings.AnnouncementHoldingSiteId}
                        label={t('AnelmaBovine:1063')}
                        onChange={(v) => false}
                        validator={validator}
                        disabled
                        value={holdingSiteId}
                    />
                </Grid>
            );
        }
        return (
            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4 }}>
                <ATextInput
                    id={Strings.AnnouncementHoldingSiteId}
                    label={t('AnelmaBovine:1063')}
                    onChange={(v) => false}
                    validator={validator}
                    disabled
                    value={holdingSiteId}
                />
            </Grid>
        );
    };

    const renderCollectWeekSelectionInputs = () => {
        return props.mode === DialogModeEnum.Create && hideCollectWeeks === false ? (
            <>
                <Grid size={12}>
                    <ALabel className='anelma-animal-announcement-general-label'>
                        {props.announcementType === AnnouncementTypeEnum.Slaughter ||
                        props.announcementType ===
                            AnnouncementTypeEnum.SlaughterWithChainInformation ||
                        props.announcementType === AnnouncementTypeEnum.ManualBovineSlaughter ||
                        props.announcementType ===
                            AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
                            ? `${t('AnelmaBovine:1043')}`
                            : `${t('AnelmaBovine:1164')}`}
                    </ALabel>
                </Grid>
                <Grid
                    size={{
                        xs: collectWeekGridSize.Xs,
                        sm: collectWeekGridSize.Sm,
                        md: collectWeekGridSize.Md,
                        lg: collectWeekGridSize.Lg
                    }}>
                    <ADropdown
                        id={Strings.AnnouncementCollectWeekStartNumber}
                        onChange={(v) =>
                            setCollectWeekStart(JSON.parse(v as string) as ICollectWeek)
                        }
                        value={JSON.stringify(
                            collectWeekStart.Week === -1 && collectWeekStart.Year === -1
                                ? { Year: '', Week: '' }
                                : collectWeekStart,
                        )}
                        items={mapCollectWeeksForDropdown(collectWeeks)}
                        validator={validator}
                        required
                        error={!collectWeekStartHasValue(collectWeekStart)}
                    />
                </Grid>
                <Grid
                    size={{
                        xs: collectWeekGridSize.Xs,
                        sm: collectWeekGridSize.Sm,
                        md: collectWeekGridSize.Md,
                        lg: collectWeekGridSize.Lg
                    }}>
                    <ADropdown
                        id={Strings.AnnouncementCollectWeekEndNumber}
                        onChange={(v) => setCollectWeekEnd(JSON.parse(v as string) as ICollectWeek)}
                        value={JSON.stringify(
                            collectWeekEnd.Week === -1 && collectWeekEnd.Year === -1
                                ? { Year: '', Week: '' }
                                : collectWeekEnd,
                        )}
                        items={mapCollectWeeksForDropdown(collectWeeks)}
                        validator={validator}
                        required
                        error={!collectWeekEndHasValue(collectWeekEnd)}
                    />
                </Grid>
            </>
        ) : null;
    };

    const getAnnouncementRejectDialogContent = (): string => {
        return announcementType === AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            ? t('AnelmaBovine:1211')
            : t('AnelmaBovine:1210');
    };

    const getAnnouncementNumber = (announcementData: IAnimalAnnouncement) => {
        return announcementData && announcementData?.Number > 0
            ? `${announcementData.Number}`
            : (isPreliminaryAnnouncement(announcementData.Type) && announcementData.PreliminaryNumber != null)
                ? `${announcementData.PreliminaryNumber}`
                : '';
    }

    return (
        <>
            <ADialogContent size={'xl'} isLoading={isLoading}>
                {salesTypeAnnotations.length > 0 ? (
                    <Annotation Annotations={salesTypeAnnotations} />
                ) : null}
                <Grid container direction='row'>
                    <Grid
                        size={{
                            xs: announcementNumberGridSize.Xs,
                            sm: announcementNumberGridSize.Sm,
                            md: announcementNumberGridSize.Md,
                            lg: announcementNumberGridSize.Lg
                        }}>
                        <ATextInput
                            id={Strings.AnnouncementNumber}
                            label={t('AnelmaBovine:1028')}
                            onChange={(v) => null} // changed from Tiltu only, we cannot change the value
                            validator={validator}
                            disabled
                            value={getAnnouncementNumber(announcementData)}
                        />
                    </Grid>
                    {renderHoldingSiteComponent()}
                    {renderCollectWeekSelectionInputs()}
                    {boundariesLoaded && (
                        <Grid size={{ xs: 12, sm: 12, md: 10, lg: 8 }}>
                            <ATextInput
                                id={Strings.AnnouncementAdditionalInformation}
                                label={t('AnelmaBovine:1039')}
                                onChange={(v) => (announcementData.Info = v)}
                                value={announcementData.Info}
                                validator={validator}
                                maxLength={addInfoMaxLength}
                                minLength={addInfoMinLength}
                                disabled={props.readOnly && props.readOnly === true}
                            />
                        </Grid>
                    )}
                </Grid>
                <Grid
                    style={{ display: 'flex', flexFlow: 'column-reverse', alignItems: 'self-end' }}
                >
                    <Grid style={{ paddingLeft: '20px' }}>
                        <AImageList
                            files={preview.file}
                            cols={5}
                            gap={10}
                            imgMaxHeight={50}
                            imgMaxWidth={50}
                            onImageClick={downLoadImage}
                        ></AImageList>
                    </Grid>
                    <Grid style={{ paddingLeft: '20px', paddingRight: '40px' }}>
                        <ADefaultButton
                            onClick={() => setAddDialogVisibility(true)}
                            icon={<EditIcon />}
                            children={t('AnelmaBovine:1038')}
                        ></ADefaultButton>
                    </Grid>
                </Grid>
                <Grid
                    size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                    sx={{ paddingLeft: '20px', paddingRight: '20px' }}
                >
                    <AnimalAnnouncementDataGrid
                        announcementType={announcementType}
                        mode={props.mode}
                        initiallySelectedAnimals={getAnnouncementAnimals(announcementData)}
                        animalAnnouncementData={announcementData}
                        allAnimals={props.animals}
                        allowedSalesTypes={allowedSalesTypes}
                        editSalesType={(animalId: string) => {
                            const animal = props.animals?.find((x) => x.Id === animalId);
                            if (!animal) return;
                            setAnimalsForDialog([animal]);
                            setSalesTypeDialogVisible(() => true);
                        }}
                        handleSelectedAnimals={(animalData: IAnimalSummary[]) =>
                            setSelectedAnimals(animalData)
                        }
                        handleAnimalRemoveFromAnnouncement={removeAnimalFromAnnouncement}
                        loading={props.loading || producerReturnIsLoading || !!isLoading}
                        readOnly={readOnlyMode || !holdingSiteIsValid}
                        farmDetails={farmDetails}
                        producerReturn={producerReturn}
                    />
                </Grid>
            </ADialogContent>

            <ADialogActions buttons={dialogActionButtons} />
            <AdditionalDialogActionButtons
                announcementType={announcementType}
                mode={props.mode}
                handleSalesTypeDialogVisibility={(value) => setSalesTypeDialogVisible(value)}
                handleCollectWeekDialogVisibility={(value) => setCollectWeekDialogVisible(value)}
                readOnly={
                    readOnlyMode ||
                    !holdingSiteIsValid ||
                    !animalAnnouncementCreationViaMmmfiIsValid(props.canCreateAnimalAnnouncement)
                }
                isLoading={isLoading}
            />
            {addAnimalDialogVisible && (
                <AddAnimalDialog
                    onClose={() => setAddAnimalDialogVisible(false)}
                    animals={omitAnimalAnnouncementAnimalsFromSelectableAnimals(
                        announcementData,
                        props.animals || [],
                    )}
                    fromDialog={SecondaryDialogTypeEnum.SlaughterOrIntermediationAnnouncement}
                    handleAnimalDataChange={handleAnimalDataChange}
                    holdingSite={holdingSiteId}
                />
            )}
            {addDialogVisibility && (
                <Grid>
                    <DocumentAddDialog
                        config={'images'}
                        data={preview}
                        initialType={null}
                        onClose={() => setAddDialogVisibility(false)}
                        onSave={filesToSaveHandler}
                        validator={new FormValidator()}
                        saveForPreview={true}
                    />
                </Grid>
            )}
            {chainInformationDialogVisible && announcementData && (
                <ChainInformationDialog
                    onClose={(cancelled, relatedAnnouncementId) => {
                        setChainInformationDialogVisible(false);

                        let preliminaryMainTypes = [
                            AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement,
                            AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
                        ];

                        // chain info, in create-mode, is only opened as part of the post-save. close regardless of cancellation because this dialog is in an invalid state after
                        // saving, and incapable of actually committing changes.
                        if (props.mode === DialogModeEnum.Create || !cancelled) {
                            if (preliminaryMainTypes.includes(announcementType))
                                props.onClose(true);
                            else
                                props.onClose();
                        }
                    }}
                    announcementData={announcementData}
                    announcementId={announcementData.Id}
                    announcementNumber={String(announcementData.Number)}
                    animals={getAnnouncementAnimals(announcementData)}
                    forceReadOnly={readOnlyMode}
                    newAnnouncement={props.mode === DialogModeEnum.Create}
                    validator={new FormValidator()}
                    medicationNumberFieldValidator={new FormValidator()}
                ></ChainInformationDialog>
            )}
            {salesTypeDialogVisible && <ModifySalesTypeDialog {...salesTypeDialogProps} />}
            {collectWeekDialogVisible && (
                <ModifyCollectWeekDialog {...modifyCollectWeekDialogProps} />
            )}
            {chainInfoConfirmDialogVisible && (
                <MidSaveConfirmDialog
                    text={t('AnelmaAnimalAnnouncement:1008')}
                    setVisible={setChainInfoConfirmDialogVisible}
                    onConfirm={() => handleSave(true)}
                />
            )}
            {displayRejectDialog && (
                <ConfirmDialog
                    data-robot-id='bovine-announcement-reject-dialog'
                    title={'AnelmaBovine:1201'}
                    content={getAnnouncementRejectDialogContent()}
                    actionButtonType={'ok'}
                    onClose={() => setDisplayRejectDialog(false)}
                    onConfirm={() => rejectPreliminaryAnnouncement(announcementData.Id)}
                />
            )}
            {producerReturnDialogVisible && farmDetails && (
                <ProducerReturnDialog
                    data={producerReturn}
                    farm={farmDetails}
                    announcementId={announcementData.Id}
                    selectedAnimalCollectDate={getSelectedAnimalCollectDate()}
                    selectedAnimalEuIdentifier={
                        selectedAnimals.length > 0 ? selectedAnimals[0].EuIdentifier : null
                    }
                    validator={new FormValidator()}
                    onClose={() => {
                        loadProducerReturn();
                        setProducerReturnDialogVisible(false);
                    }}
                    isNew={producerReturn === null}
                    readonly={
                        (producerReturn === null && !auth.canCreateProducerReturn) ||
                        (producerReturn !== null && !auth.canUpdateProducerReturn) ||
                        producerRetrunAnimalIsLO()
                    }
                />
            )}
        </>
    );
}
